/** Ported 1:1 from app/templates/components/visualization/mini-bar-chart.html
 *  (Phase 4 of the port). Apple-Health-style histograms and the WHOOP-style
 *  score bars: same imperative render-into-container-by-id API the Jinja
 *  page scripts called, same 15-minute bucketing. CSS: legacy-css/
 *  mini-bar-chart.css.
 */
import { ScoreRing, type ScoreThresholds } from '../../lib/viz/scoreRing'

export type BarDatum = { value: number; label?: string | null; tooltip?: string | null }
export type TimeRange = { from: number; to: number; preset?: string } | null

/** Bucket indices (0-5) covered by a time range; full match when null. */
export function getBucketIndices(timeRange: TimeRange): number[] {
  if (!timeRange || (timeRange.from === 0 && timeRange.to === 90)) {
    return [0, 1, 2, 3, 4, 5]
  }
  const startBucket = Math.floor(timeRange.from / 15)
  const endBucket = Math.min(Math.ceil(timeRange.to / 15) - 1, 5)
  const indices: number[] = []
  for (let i = startBucket; i <= endBucket; i++) indices.push(i)
  return indices
}

/** The caption a chart carries when its moments cannot be placed in time
 *  (no frame rate from the server): six empty intervals alone read as
 *  "nothing happened", which is a different fact. One string for every
 *  surface, kept in lib/frameRate. */
export { NO_FRAME_RATE_NOTE } from '../../lib/frameRate'

/** Append the note to a chart container and mark it unplaced. */
function appendNote(container: HTMLElement, note: string | undefined): void {
  if (!note) { delete container.dataset.unplaced; return }
  container.dataset.unplaced = 'true'
  const p = document.createElement('p')
  p.className = 'mini-bar-chart__note'
  p.setAttribute('role', 'note')
  p.textContent = note
  container.appendChild(p)
}

/** Render a mini bar chart into #containerId. */
export function render(containerId: string, data: BarDatum[],
                       options: { labelMap?: Record<number, string>;
                                  note?: string } = {}): void {
  const container = document.getElementById(containerId)
  if (!container) return

  // Create structure if it doesn't exist (dynamic rendering into empty
  // containers, exactly as the Jinja component did).
  if (!container.querySelector('.mini-bar-chart__bars')) {
    container.innerHTML = `
        <div class="mini-bar-chart">
          <div class="mini-bar-chart__bars"></div>
          <div class="mini-bar-chart__labels"></div>
        </div>
      `
  }

  container.querySelector('.mini-bar-chart__note')?.remove()
  const chartEl = container.querySelector('.mini-bar-chart') ?? container
  const barsEl = chartEl.querySelector('.mini-bar-chart__bars')
  const labelsEl = chartEl.querySelector('.mini-bar-chart__labels')
  if (!barsEl) return

  const maxValue = Math.max(...data.map((d) => d.value || 0), 1)

  // Default label positions: 15' (index 0), HT (index 2), 75' (index 4)
  const labelMap = options.labelMap ?? { 0: "15'", 2: 'HT', 4: "75'" }

  barsEl.innerHTML = ''
  if (labelsEl) labelsEl.innerHTML = ''

  data.forEach((item) => {
    const bar = document.createElement('div')
    bar.className = 'mini-bar-chart__bar'

    if (!item.value || item.value === 0) {
      bar.dataset.empty = 'true'
    } else {
      const heightPct = (item.value / maxValue) * 100
      bar.style.height = `${Math.max(heightPct, 8)}%`
      if (item.tooltip) {
        const tooltip = document.createElement('span')
        tooltip.className = 'mini-bar-chart__tooltip'
        tooltip.textContent = item.tooltip
        bar.appendChild(tooltip)
      }
    }
    barsEl.appendChild(bar)
  })

  if (labelsEl && Object.keys(labelMap).length > 0) {
    data.forEach((_, i) => {
      const labelEl = document.createElement('span')
      labelEl.className = 'mini-bar-chart__label'
      labelEl.textContent = labelMap[i] ?? ''
      labelsEl.appendChild(labelEl)
    })
  }
  appendNote(container, options.note)
}

/** The 15-minute interval (0-5, the last open-ended) a frame falls in, at
 *  the game's frame rate. */
export function intervalOf(frame: number, fps: number): number {
  return Math.min(Math.floor(frame / fps / 60 / 15), 5)
}

/** Bucket moments into 15-minute intervals, filtered by type + time range.
 *  Every interval empty (labelled, no value, no tooltip) when the server
 *  sent no frame rate: a moment cannot be placed in an interval without
 *  one, and a guessed 25 misplaced a 30 fps game's. The same empty
 *  intervals intervalScores draws then, so the charts agree. */
export function bucketMoments(moments: { frame?: number; type?: string }[],
                              filterType: string | null, timeRange: TimeRange,
                              options: { fps: number | null }): BarDatum[] {
  const buckets = [0, 0, 0, 0, 0, 0]
  const allLabels = ["0-15'", "15-30'", "30-45'", "45-60'", "60-75'", "75-90'"]
  const { fps } = options
  if (!fps) {
    return getBucketIndices(timeRange).map((idx) => ({
      value: 0, label: allLabels[idx]!, tooltip: null }))
  }

  moments.forEach((m) => {
    if (filterType && m.type !== filterType) return
    buckets[intervalOf(m.frame || 0, fps)]!++
  })

  const indices = getBucketIndices(timeRange)
  return indices.map((idx) => ({
    value: buckets[idx]!,
    label: allLabels[idx]!,
    tooltip: buckets[idx]! > 0 ? `${buckets[idx]} moments in ${allLabels[idx]}` : null,
  }))
}

/** Mean score (0-100) of the scored moments in each 15-minute interval;
 *  an interval with none shows `overall` (0-1), or null. All null when the
 *  server sent no frame rate, rather than placing moments by a guess. */
export function intervalScores(moments: { frame?: number; score?: number | null }[],
                               fps: number | null, overall: number | null): (number | null)[] {
  if (!fps) return [null, null, null, null, null, null]
  const buckets: number[][] = [[], [], [], [], [], []]
  moments.forEach((m) => {
    if (typeof m.score === 'number') buckets[intervalOf(m.frame || 0, fps)]!.push(m.score)
  })
  return buckets.map((bkt) => {
    if (bkt.length === 0) return overall !== null ? overall * 100 : null
    return (bkt.reduce((a, c) => a + c, 0) / bkt.length) * 100
  })
}

/** Render WHOOP-style score bars (0-100 per interval; null = no data).
 *  Coloured on `thresholds` when passed (the bands ScoreRing's 'dynamic'
 *  variant takes), else on the bands of `variant`. */
export function renderScores(containerId: string, scores: (number | null)[],
                             options: { variant?: string; thresholds?: ScoreThresholds;
                                        timeRange?: TimeRange; note?: string } = {}): void {
  const container = document.getElementById(containerId)
  if (!container) return

  const timeRange = options.timeRange ?? null
  const allLabels = ["15'", "30'", 'HT', "60'", "75'", "90'"]
  const allTimeRanges = ["0'-15'", "15'-30'", "30'-45'", "45'-60'", "60'-75'", "75'-90'"]
  const allStartLabels = ["0'", "15'", "30'", 'HT', "60'", "75'"]

  const indices = getBucketIndices(timeRange)

  const filteredScores = indices.map((i) => scores[i] ?? null)
  const labels = indices.map((i) => allLabels[i]!)
  const timeRangesFiltered = indices.map((i) => allTimeRanges[i]!)
  const startLabel = allStartLabels[indices[0]!] ?? "0'"

  // Thresholds passed, else from the centralized ScoreRing config, 0-1 → 0-100.
  const thresholds = options.thresholds ?? ScoreRing.getThresholds(options.variant ?? 'default')
  const GOOD = thresholds.good * 100
  const OKAY = thresholds.okay * 100

  container.innerHTML = `
      <div class="score-bar-chart">
        <div class="score-bar-chart__bars"></div>
        <div class="score-bar-chart__labels">
          <span class="score-bar-chart__label--start">${startLabel}</span>
        </div>
      </div>
    `

  const barsEl = container.querySelector('.score-bar-chart__bars')!
  const labelsEl = container.querySelector('.score-bar-chart__labels')!

  // Relative scaling: max value → 90% height, with a 30% floor.
  const validScores = filteredScores.filter((s): s is number => s !== null && s !== undefined)
  const maxScore = validScores.length > 0 ? Math.max(...validScores) : 100
  const MAX_HEIGHT = 90
  const MIN_HEIGHT = 30

  filteredScores.forEach((score, i) => {
    const bar = document.createElement('div')
    bar.className = 'score-bar-chart__bar'

    if (score === null || score === undefined) {
      bar.dataset.empty = 'true'
    } else {
      const rawPct = (score / maxScore) * MAX_HEIGHT
      const pct = Math.max(rawPct, MIN_HEIGHT)
      bar.style.height = `${pct}%`
      bar.classList.add(score >= GOOD ? 'score-bar-chart__bar--good'
        : score >= OKAY ? 'score-bar-chart__bar--okay' : 'score-bar-chart__bar--bad')

      const label = document.createElement('span')
      label.className = 'score-bar-chart__value'
      label.textContent = String(Math.round(score))
      bar.appendChild(label)

      const tooltip = document.createElement('span')
      tooltip.className = 'score-bar-chart__tooltip'
      tooltip.textContent = `Average score in ${timeRangesFiltered[i]}`
      bar.appendChild(tooltip)
    }
    barsEl.appendChild(bar)

    const labelEl = document.createElement('span')
    labelEl.textContent = labels[i]!
    labelsEl.appendChild(labelEl)
  })
  appendNote(container, options.note)
}
